import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, tap, shareReplay, catchError } from 'rxjs/operators';
import { TruthManagement } from './truth-management';
import { DashboardEvalFile } from '../models/truth-management.model';

interface CachedFiles {
  files: DashboardEvalFile[];
  timestamp: number;
  observable$: Observable<DashboardEvalFile[]>;
}

interface LoadingState {
  isLoading: boolean;
  error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentFilesService {
  private readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache
  
  // Cache storage: Map<documentModel, CachedFiles>
  private filesCache = new Map<string, CachedFiles>();
  
  // Loading states: Map<documentModel, BehaviorSubject<LoadingState>>
  private loadingStates = new Map<string, BehaviorSubject<LoadingState>>();
  
  constructor(private truthManagementService: TruthManagement) {
  }
  
  /**
   * Get loading state observable for a document model
   * Used by components to show/hide loaders
   */
  getLoadingState(documentModel: string): Observable<LoadingState> {
    if (!this.loadingStates.has(documentModel)) {
      this.loadingStates.set(documentModel, new BehaviorSubject<LoadingState>({
        isLoading: false,
        error: null
      }));
    }
    return this.loadingStates.get(documentModel)!.asObservable();
  }
  
  /**
   * Get files for a document model with caching
   * API Used: GET /api/v1/eval/files?document_model={documentModel}
   */
  getFiles(documentModel: string, forceRefresh = false): Observable<DashboardEvalFile[]> {
    if (!documentModel) {
      return of([]);
    }
    
    // Check cache validity
    const cached = this.filesCache.get(documentModel);
    const now = Date.now();
    const isCacheValid = cached && !forceRefresh && (now - cached.timestamp) < this.CACHE_TTL_MS;
    
    if (isCacheValid && cached) {
      // Return cached observable (already has shareReplay)
      return cached.observable$;
    }
     
    // Set loading state
    this.setLoadingState(documentModel, true, null);
    
    // Create new observable with shareReplay
    const files$ = this.truthManagementService.getEvaluationFiles(documentModel).pipe(
      map(evaluationFiles => { 
        // Transform EvaluationFile[] to DashboardEvalFile[] format
        const dashboardFiles: DashboardEvalFile[] = evaluationFiles.map(ef => ({
          uuid: ef.uuid,
          file_name: ef.file_name,
          document_model: ef.document_model || documentModel,
          created_at: ef.created_at || new Date().toISOString(),
          attribute_count: 0,
          attributes: []
        }));
        return dashboardFiles;
      }),
      tap(files => {
       // Clear loading state on success
        this.setLoadingState(documentModel, false, null);
      }),
      catchError(error => {
        // Set error state
        this.setLoadingState(documentModel, false, 'Failed to load files');
        return of([]);
      }),
      shareReplay(1) // Share the result with all subscribers
    );
    
    // Store in cache with observable
    this.filesCache.set(documentModel, {
      files: [], // Will be populated by first subscriber
      timestamp: now,
      observable$: files$
    });
    
    // Subscribe to populate cache.files array
    files$.subscribe(files => {
      const cached = this.filesCache.get(documentModel);
      if (cached) {
        cached.files = files;
      }
    });
    
    return files$;
  }
  
  /**
   * Clear cache for a specific document model
   */
  clearCache(documentModel: string): void {
    this.filesCache.delete(documentModel);
    this.loadingStates.delete(documentModel);
  }
  
  /**
   * Clear all cached data
   */
  clearAllCache(): void {
    this.filesCache.clear();
    this.loadingStates.clear();
  }
  
  /**
   * Set loading state for a document model
   */
  private setLoadingState(documentModel: string, isLoading: boolean, error: string | null): void {
    if (!this.loadingStates.has(documentModel)) {
      this.loadingStates.set(documentModel, new BehaviorSubject<LoadingState>({
        isLoading: false,
        error: null
      }));
    }
    
    const state = this.loadingStates.get(documentModel)!;
    state.next({ isLoading, error });
    }
  
  /**
   * Check if files are cached for a document model
   */
  isCached(documentModel: string): boolean {
    const cached = this.filesCache.get(documentModel);
    if (!cached) return false;
    
    const now = Date.now();
    return (now - cached.timestamp) < this.CACHE_TTL_MS;
  }
  
  /**
   * Get cache info for debugging
   */
  getCacheInfo(): { documentModel: string; fileCount: number; age: number }[] {
    const now = Date.now();
    return Array.from(this.filesCache.entries()).map(([documentModel, cached]) => ({
      documentModel,
      fileCount: cached.files.length,
      age: Math.round((now - cached.timestamp) / 1000) // seconds
    }));
  }
}